// src/services/maps/leafletCore.js
// Leaflet map lifecycle management.
// Owns the L.map instance — components never create one directly.
//
// Pattern: MapManager.init(container) returns the map.
//          MapManager.destroy() cleans up properly.
//          All other map modules (overlays, clustering, events) receive
//          the map instance from here — they don't create their own.

import L from 'leaflet';
import { captureMapError, breadcrumb } from '@services/monitoring';
import 'leaflet/dist/leaflet.css';
import { MAP_DEFAULTS, DEFAULT_PROVIDER, ZOOM_THRESHOLDS } from './config.js';

// Fix Leaflet's default icon path resolution (breaks with bundlers)
// Must run before any L.icon() call.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl:       new URL('leaflet/dist/images/marker-icon.png',    import.meta.url).href,
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  shadowUrl:     new URL('leaflet/dist/images/marker-shadow.png',  import.meta.url).href,
});

// ── Singleton map instance ────────────────────────────────────────
let _map         = null;
let _baseLayer   = null;
let _floodLayer  = null;
let _container   = null;
let _resizeObserver = null;

export const MapManager = {

  // ── Initialize ──────────────────────────────────────────────────
  // containerEl: the DOM element to mount into
  // opts: overrides for MAP_DEFAULTS
  init(containerEl, opts = {}) {
    if (_map) {
      // Already initialized — re-use if container is the same
      if (_container === containerEl) return _map;
      MapManager.destroy();
    }

    _container = containerEl;

    _map = L.map(containerEl, {
      ...MAP_DEFAULTS,
      ...opts,
      zoomControl:       false,    // we supply our own UI
      attributionControl: false,   // we supply a custom attribution bar
    });

    // Base tile layer
    _baseLayer = L.tileLayer(DEFAULT_PROVIDER.url, {
      attribution: DEFAULT_PROVIDER.attribution,
      maxZoom:     DEFAULT_PROVIDER.maxZoom,
      // Tell the browser to cache tiles aggressively
      crossOrigin: true,
    }).addTo(_map);

    // Attribution control positioned bottom-left, minimal
    L.control.attribution({ position: 'bottomleft', prefix: false }).addTo(_map);

    // Resize observer — keeps map filling its container when shell resizes
    _resizeObserver = new ResizeObserver(() => _map?.invalidateSize());
    _resizeObserver.observe(containerEl);

    // Leaflet emits errors via console — intercept tile errors for Sentry
    _map.on('tileerror', (e) => {
      captureMapError(new Error('Tile load failed: ' + e.tile?.src), {
        tileUrl: e.tile?.src,
        zoom:    _map.getZoom(),
      });
    });

    breadcrumb('Map initialized', { zoom: MAP_DEFAULTS.zoom });
    return _map;
  },

  // ── Get instance (null if not initialized) ───────────────────────
  get() { return _map; },

  // ── FEMA Flood Zone overlay ──────────────────────────────────────
  // Uses FEMA NFHL WMS via Leaflet's built-in WMS tile layer.
  // WMS tile requests are image fetches — no CORS issues.
  // Layer 28 = Special Flood Hazard Areas (A/AE/AO/VE zones)
  addFloodZoneLayer() {
    if (!_map || _floodLayer) return;
    try {
      const wmsUrl = 'https://hazards.fema.gov/arcgis/rest/services/public/NFHL/MapServer/WMSServer';
      if (typeof L.tileLayer.wms !== 'function') {
        console.warn('[MapManager] L.tileLayer.wms not available');
        return;
      }
      _floodLayer = L.tileLayer.wms(wmsUrl, {
        layers:      '28',
        format:      'image/png',
        transparent:  true,
        opacity:      0.5,
        version:     '1.3.0',
        attribution: 'FEMA NFHL',
        maxZoom:      20,
      }).addTo(_map);
      _floodLayer.setZIndex(5);
    } catch (e) {
      console.error('[MapManager] addFloodZoneLayer failed:', e.message);
      _floodLayer = null;
    }
  },

  removeFloodZoneLayer() {
    if (!_map || !_floodLayer) return;
    _map.removeLayer(_floodLayer);
    _floodLayer = null;
  },

  // ── Destroy ──────────────────────────────────────────────────────
  destroy() {
    _resizeObserver?.disconnect();
    _resizeObserver = null;
    _map?.remove();
    _map        = null;
    _baseLayer  = null;
    _floodLayer = null;
    _container  = null;
  },

  // ── Switch base tile provider ─────────────────────────────────────
  setProvider(provider) {
    if (!_map) return;
    if (_baseLayer) _map.removeLayer(_baseLayer);
    _baseLayer = L.tileLayer(provider.url, {
      attribution: provider.attribution,
      maxZoom:     provider.maxZoom,
      crossOrigin: true,
    }).addTo(_map);
    _baseLayer.setZIndex(0);
  },

  // ── Fly to a coordinate ───────────────────────────────────────────
  flyTo(lat, lon, zoom, opts = {}) {
    _map?.flyTo([lat, lon], zoom ?? _map.getZoom(), {
      duration: opts.duration ?? 0.8,
      easeLinearity: 0.5,
    });
  },

  // ── Fit bounds ────────────────────────────────────────────────────
  fitBounds(bounds, opts = {}) {
    if (!_map || !bounds) return;
    _map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, ...opts });
  },

  // ── Viewport info ─────────────────────────────────────────────────
  getBounds()       { return _map?.getBounds() ?? null; },
  getCenter()       { return _map?.getCenter() ?? null; },
  getZoom()         { return _map?.getZoom() ?? MAP_DEFAULTS.zoom; },
  isAboveThreshold: (t) => (_map?.getZoom() ?? 0) >= t,
};

// ── Marker icon factory ───────────────────────────────────────────
// All icon creation goes through this — never call L.icon() in components.
// Returns L.DivIcon (no external image dependency → works fully offline).

export function makeDivIcon(emoji, color, size = 32, opts = {}) {
  const s = size;
  const pin = opts.noPin ? '' : `<polygon points="${s/2-5},${s} ${s/2+5},${s} ${s/2},${s+8}" fill="${color}"/>`;
  const html = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s + (opts.noPin ? 0 : 8)}"
         viewBox="0 0 ${s} ${s + (opts.noPin ? 0 : 8)}">
      <circle cx="${s/2}" cy="${s/2}" r="${s/2-2}"
              fill="${color}" stroke="#fff" stroke-width="2"/>
      <text x="${s/2}" y="${s/2+5}"
            font-size="${Math.round(s*0.52)}"
            text-anchor="middle" font-family="system-ui">${emoji}</text>
      ${pin}
    </svg>`;
  return L.divIcon({
    html,
    className:   '',
    iconSize:    [s, s + (opts.noPin ? 0 : 8)],
    iconAnchor:  [s / 2, s + (opts.noPin ? 0 : 8)],
    popupAnchor: [0, -(s + 4)],
  });
}

// Pre-built icon set for the most common marker types
export const ICONS = {
  sos:          () => makeDivIcon('🚨', '#7f1d1d', 36),
  flood:        () => makeDivIcon('🌊', '#1d4ed8', 30),
  road:         () => makeDivIcon('🚗', '#ea580c', 28),
  power:        () => makeDivIcon('⚡', '#7c3aed', 28),
  fire:         () => makeDivIcon('🔥', '#dc2626', 30),
  gas_open:     () => makeDivIcon('⛽', '#059669', 28),
  gas_busy:     () => makeDivIcon('⛽', '#d97706', 28),
  gas_closed:   () => makeDivIcon('⛽', '#6b7280', 28),
  shelter_open: () => makeDivIcon('🏠', '#059669', 30),
  shelter_full: () => makeDivIcon('🏠', '#d97706', 30),
  shelter_closed:()=> makeDivIcon('🏠', '#6b7280', 28),
  hospital:     () => makeDivIcon('🏥', '#0369a1', 30),
  gauge_normal: () => makeDivIcon('💧', '#059669', 24, { noPin: true }),
  gauge_alert:  () => makeDivIcon('💧', '#d97706', 24, { noPin: true }),
  gauge_flood:  () => makeDivIcon('💧', '#dc2626', 28, { noPin: true }),
  user:         () => makeDivIcon('📍', '#0369a1', 28, { noPin: true }),
  // Connectivity icons — status-aware
  wifi_active:    () => makeDivIcon('📶', '#0369a1', 28, { noPin: true }),
  wifi_likely:    () => makeDivIcon('📶', '#d97706', 26, { noPin: true }),
  wifi_offline:   () => makeDivIcon('📶', '#6b7280', 24, { noPin: true }),
  wifi_shelter:   () => makeDivIcon('🏠', '#059669', 28, { noPin: true }),
  charging_active:() => makeDivIcon('🔌', '#7c3aed', 28, { noPin: true }),
  charging_likely:() => makeDivIcon('🔌', '#d97706', 26, { noPin: true }),
  starlink_active:() => makeDivIcon('🛰', '#1d4ed8', 30, { noPin: true }),
  starlink_likely:() => makeDivIcon('🛰', '#6b7280', 26, { noPin: true }),
  open_business:  () => makeDivIcon('🏪', '#d97706', 26, { noPin: true }),
  lte_recovery:   () => makeDivIcon('📡', '#dc2626', 28, { noPin: true }),
};

// ── Popup template builder ────────────────────────────────────────
// All popups use this — no HTML string building in components.
export function buildPopup(opts = {}) {
  const {
    title = '', subtitle = '', body = '',
    callUrl, directionsUrl, callLabel = 'Call', directionsLabel = 'Directions',
    color = '#d97706',
  } = opts;

  const btnStyle = (bg) =>
    `style="display:inline-block;padding:6px 12px;border-radius:7px;
     background:${bg};color:#fff;text-decoration:none;
     font-size:12px;font-weight:800;font-family:system-ui"`;

  return `
    <div style="font-family:system-ui;min-width:180px;max-width:240px">
      ${title    ? `<div style="font-weight:900;font-size:14px;color:#1c1917;margin-bottom:2px">${title}</div>` : ''}
      ${subtitle ? `<div style="font-size:11px;color:#78716c;margin-bottom:6px">${subtitle}</div>` : ''}
      ${body     ? `<div style="font-size:12px;color:#44403c;line-height:1.5;margin-bottom:8px">${body}</div>` : ''}
      ${(callUrl || directionsUrl) ? `
        <div style="display:flex;gap:6px">
          ${callUrl        ? `<a href="${callUrl}" ${btnStyle('#dc2626')}>${callLabel}</a>` : ''}
          ${directionsUrl  ? `<a href="${directionsUrl}" target="_blank" ${btnStyle(color)}>${directionsLabel}</a>` : ''}
        </div>` : ''}
    </div>`;
}
